// Feature Documentation: features/nma-v3-icd10-pipeline.md
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Box, Heading, Table, Thead, Tbody, Tr, Th, Td, Spinner, Text, Badge, Button, HStack } from '@chakra-ui/react';
import { AppContext } from './AppContext';
import { callApi } from '../callApi';
import HeaderMenus from './HeaderMenus';

const NMAV2QualityDashboard = () => {
  const { accessToken } = useContext(AppContext);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'service_date', direction: 'desc' });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await callApi('/nma/v3/coding-quality', accessToken);
        if (response.ok) {
          const result = await response.json();
          setData(result.data || []);
        }
      } catch (error) {
        console.error("Error fetching V3 quality data:", error);
      } finally {
        setLoading(false);
      }
    };
    if (accessToken) fetchData();
  }, [accessToken]);

  const sortedData = useMemo(() => {
    let sortableItems = [...data];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        const aIsEmpty = aValue === null || aValue === undefined || aValue === '';
        const bIsEmpty = bValue === null || bValue === undefined || bValue === '';

        if (aIsEmpty && bIsEmpty) return 0;
        if (aIsEmpty) return 1;
        if (bIsEmpty) return -1;

        const numericColumns = ['v3_preference_score', 'pid'];
        if (numericColumns.includes(sortConfig.key)) {
          const aNum = parseFloat(aValue);
          const bNum = parseFloat(bValue);

          const aIsNaN = isNaN(aNum);
          const bIsNaN = isNaN(bNum);

          if (aIsNaN && bIsNaN) return 0;
          if (aIsNaN) return 1;
          if (bIsNaN) return -1;

          return sortConfig.direction === 'asc' ? aNum - bNum : bNum - aNum;
        }

        const aString = String(aValue);
        const bString = String(bValue);
        const comparison = aString.localeCompare(bString, undefined, { numeric: true, sensitivity: 'base' });

        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
    }
    return sortableItems;
  }, [data, sortConfig]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIndicator = (key) => {
    if (!sortConfig || sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  return (
    <Box minH="100vh" bg="gray.50">
      <HeaderMenus />
      <Box maxW="1500px" mx="auto" p={6}>
        <Heading size="lg" mb={6} color="gray.700">NMA V3 Pipeline Results (Advanced vs V3)</Heading>

        {loading ? (
          <Spinner size="xl" />
        ) : data.length === 0 ? (
          <Text>No V3 cases found.</Text>
        ) : (
          <Box bg="white" shadow="sm" borderRadius="lg" overflow="hidden">
            <Table variant="simple" size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th cursor="pointer" onClick={() => requestSort('pid')}>
                    PID{getSortIndicator('pid')}
                  </Th>
                  <Th cursor="pointer" onClick={() => requestSort('procedure_name')}>
                    Procedure Name{getSortIndicator('procedure_name')}
                  </Th>
                  <Th cursor="pointer" onClick={() => requestSort('service_date')}>
                    Date of Service{getSortIndicator('service_date')}
                  </Th>
                  <Th>Advanced Codes (A → F)</Th>
                  <Th>V3 Codes (A → F)</Th>
                  <Th isNumeric cursor="pointer" onClick={() => requestSort('v3_preference_score')}>
                    V3 Preference{getSortIndicator('v3_preference_score')}
                  </Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {sortedData.map((row) => {
                  const legacyCodes = row.legacy_codes || [];
                  const v3Codes = row.final_codes || [];
                  const pointerAChanged = legacyCodes.length > 0 && v3Codes.length > 0 && legacyCodes[0] !== v3Codes[0];
                  const prefScore = row.v3_preference_score ?? row.v2_preference_score;

                  return (
                    <Tr key={row.internal_case_id}>
                      <Td fontWeight="medium">{row.pid}</Td>
                      <Td>{row.procedure_name}</Td>
                      <Td>{row.service_date}</Td>
                      <Td>
                        <HStack spacing={1} flexWrap="wrap">
                          {legacyCodes.length > 0 ? legacyCodes.map((code, idx) => (
                            <Badge key={`${code}-${idx}`} colorScheme={idx === 0 ? 'blue' : 'gray'} fontSize="xs">
                              {code}
                            </Badge>
                          )) : <Text fontSize="xs" color="gray.400">None</Text>}
                        </HStack>
                      </Td>
                      <Td>
                        <HStack spacing={1} flexWrap="wrap">
                          {v3Codes.length > 0 ? v3Codes.map((code, idx) => (
                            <Badge
                              key={`${code}-${idx}`}
                              colorScheme={idx === 0 ? (pointerAChanged ? 'green' : 'purple') : 'gray'}
                              fontSize="xs"
                            >
                              {code}
                            </Badge>
                          )) : <Text fontSize="xs" color="gray.400">None</Text>}
                        </HStack>
                      </Td>
                      <Td isNumeric>
                        {prefScore !== null && prefScore !== undefined ? (
                          <Badge colorScheme={prefScore >= 60 ? 'green' : 'red'}>
                            {prefScore} / 100
                          </Badge>
                        ) : (
                          <Text fontSize="sm" color="gray.400">Not Run</Text>
                        )}
                      </Td>
                      <Td>
                        {row.has_legacy_run ? (
                          <Button
                            size="xs"
                            colorScheme="purple"
                            onClick={() => window.open(`/nma-compare-v3/${row.external_case_id}`, '_blank')}
                          >
                            Compare V1 vs V3
                          </Button>
                        ) : (
                          <Button
                            size="xs"
                            variant="outline"
                            colorScheme="purple"
                            onClick={() => window.open(`/nma-compare-v3/${row.external_case_id}`, '_blank')}
                          >
                            View V3
                          </Button>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default NMAV2QualityDashboard;
